/**
 * The headless "read the collections tree, apply ONE record-granular change,
 * write it back" module. Every collections write in `src/mcp/` goes through
 * `applyCollectionsChange` and there is no second path.
 *
 * ==========================================================================
 * READ THIS BEFORE CHANGING ANYTHING HERE: THE WRITE RULE IS THE INVERSE OF
 * THE ONE BOARDS FOLLOWS.
 * ==========================================================================
 *
 * `hooks/useWorkspacePersistence.js:38-73` re-saves the ENTIRE workspace —
 * collections, variables, environments, schemas — from the ZUSTAND STORE, on a
 * debounce, whenever any of those five change. And the store it reads is
 * `useCollectionsStore.collections`.
 *
 * For boards, phase 2's rule was "write the records, then publish them into the
 * open tab, because the tab's own whole-board persist would otherwise delete
 * them". Here the rule inverts:
 *
 *     AN MCP COLLECTIONS WRITE THAT REACHES STORAGE WITHOUT PASSING THROUGH
 *     `setCollections` IS REVERTED BY THE USER'S NEXT KEYSTROKE.
 *
 * Not "may be" — the effect above will write the pre-MCP array back over it,
 * and `sync/store.rs:284-310` tombstones every row absent from the array it is
 * handed (`tombstone_missing = true` for collections, requests, environments
 * and both schema families). So the store is not a cache in front of the
 * write; the store IS the write.
 *
 * ## Which is also why this module persists NOTHING itself
 *
 * This is the one place phase 5 departs from its plan, so the reasoning is
 * here rather than in a task file. PLAN §D1.8 had every handler follow
 * `setCollections` with its own `saveDbSnapshot(wsKey, { collections })`, so it
 * could await durability. Three facts, each verified in this tree, say not to:
 *
 * 1. **`saveDbSnapshot` is the SQLite backend, and most workspaces are not on
 *    it.** `services/workspaceStoreRouter.js` routes four ways — SQLite (only
 *    for workspaces enrolled in `sync_engine_v1`, which today happens ONLY in
 *    `workspaceCloudMigration.enrollCloudWorkspace`), local JSON, git YAML at
 *    `rootPath`, and cloud REST. A plain local workspace — the common case —
 *    persists to JSON files. Writing SQLite rows for it would write somewhere
 *    nothing reads.
 * 2. **The app's own local write path is store-only.** `handleCreateCollection`
 *    (`useCollections.js:452-457`) and `handleImportCollections`
 *    (`useImportExport.js:186-194`) both end at `setCollections(...)` and
 *    persist nothing; the debounced effect does it. Adding a second writer
 *    beside a working one is what `uiRefresh.js`'s own header warns against.
 * 3. **Importing the router is out of the question and importing the SQLite
 *    facade is nearly as bad.** `workspaceStoreRouter` drags the `fs` plugin
 *    and the git surface into the MCP import perimeter; `services/syncStore.js`
 *    statically imports `@tauri-apps/api/event`, which is untranspiled ESM that
 *    jest cannot parse — `importEnvironmentModal.test.js:13-16` mocks the
 *    router out for exactly that reason, and pulling it in here would break
 *    every existing MCP jest suite.
 *
 * So this module imports NO `services/` module at all and calls NO Tauri
 * command. `ALLOWED_INVOKES` in `mcpAttackSurface.test.js` is byte-identical
 * after phase 5: the perimeter does not widen by one name.
 *
 * **The cost, stated plainly.** A handler reports success when the store has
 * taken the change, not when bytes are on disk. The persist effect fires on a
 * 0 ms timeout (400 ms for a git workspace), so the window is one React commit
 * — but if the app is killed inside it, the write is gone and the agent was
 * told it landed. That is also true of every collection the USER creates in
 * the app today, which is why it is acceptable rather than merely tolerated.
 *
 * ## The workspaces this refuses, and why (O27)
 *
 * The set accepted here is EXACTLY the set `useWorkspacePersistence`'s first
 * effect covers: `usesLocalDataPath(ws, isAuthenticated)` and not git.
 *
 *   * **git** — refused. `O27`, and under this design the reason is narrower
 *     than the plan's: the app itself would write the YAML, but a git
 *     workspace's persist is debounced 400 ms behind a flush that rewrites a
 *     tree at an arbitrary path on the user's disk, and that is not something a
 *     "Collections: write" toggle describes.
 *   * **cloud REST** — refused. That path is `services/apiClient`, which the
 *     MCP perimeter pins as unreachable (**O16**), and — decisively — the
 *     persist effect above early-returns for it, so an MCP write to one would
 *     live in memory and vanish on reload. An invisible write is worse than an
 *     error.
 *   * **no workspace selected / not yet hydrated** — refused where we can see
 *     it. See `HYDRATION` below, which is honest about where we cannot.
 */
import { useCollectionsStore, setCollections } from '../store/collectionsStore';
import { patchOpenRequestTab } from './requestTabSync';
import { useWorkspaceStore } from '../store/workspaceStore';
import { useAuthStore } from '../store/authStore';
import { usesLocalDataPath, isGitWorkspace, isBackendWorkspace, isLocalWorkspace } from '../utils/workspaceStorage';
import { isTeamWorkspace } from '../utils/workspaceShareEligibility';
import { syncEngineEnabled } from '../utils/featureFlags';
import { mcpError } from './errors';
import { capRefusal, isCapped, readOnlyWorkspaceRefusal } from '../plans/capRefusal';
import { countApiItems } from '../plans/apiCounts';
import { otherLocalApiCounts } from '../plans/otherLocalApiCounts';
import { gitTrackingReport } from '../services/git/gitTracking';
import { REV_RE } from './collectionRev';
import { OUTBOX_ITEM_MAX_BYTES } from '../records/syncLimits';

/**
 * `sync/mod.rs`'s `OUTBOX_ITEM_MAX_BYTES`, mirrored (the value lives in
 * `records/syncLimits.js`, shared with the web records backend).
 *
 * The mirror is PINNED by a cross-language source scan
 * (`mcpCollectionSize.test.js`), in the shape `mailCatcherSpamRules.test.js`
 * established, because the two numbers disagreeing is silent.
 *
 * Why a JS pre-check exists at all: over the cap, `append_outbox`
 * (`sync/store.rs:103-110`) LOGS AND RETURNS `Ok(())`. The local row is
 * written and the sync op is never queued — so an oversize request exists on
 * one device and nowhere else, with no error anywhere. `records.rs`'s
 * `cas_upsert_in` refuses; this path does not. That is a pre-existing app
 * defect this module refuses around rather than fixes.
 */
export { OUTBOX_ITEM_MAX_BYTES };

/**
 * MCP-vs-MCP serialisation. Every handler runs in the one renderer, so a
 * promise chain is a STRUCTURAL answer here rather than an approximation: two
 * in-flight read-modify-writes over the same array cannot interleave.
 *
 * It says nothing about MCP-vs-USER — the user editing a collection name in
 * the 50 ms this takes wins or loses on ordinary last-write-wins, exactly as
 * two of their own devices already do. Tranche 1 is create-only, so the worst
 * case is a created record landing on a slightly older tree; tranche 3's
 * `expected_rev` is what narrows that for updates.
 */
let queue = Promise.resolve();

function serialize(fn) {
  const run = queue.then(fn, fn);
  // Keep the chain alive whatever `fn` does; the caller still sees the error.
  queue = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

/** What every collections result carries instead of a `scope` (**O27**). */
export function workspaceReport(workspace) {
  return {
    id: String(workspace?.id ?? ''),
    name: String(workspace?.name || ''),
    storage: storageKindOf(workspace),
    git: gitTrackingReport(workspace),
  };
}

// `gitTrackingReport` moved to services/git/gitTracking.js (04.10.2026) so the
// in-app AI's get_git_status reports the same four facts; re-exported here.
export { gitTrackingReport };

/**
 * The storage label reported to the agent, and the one the refusal names so a
 * user can act on it. Derived from the app's own predicates — never from
 * `workspace.storage` alone, which says `local` for an enrolled workspace too.
 */
export function storageKindOf(workspace) {
  if (isGitWorkspace(workspace)) return 'git';
  const authed = !!useAuthStore.getState().isAuthenticated;
  if (isBackendWorkspace(workspace, authed)) {
    return syncEngineEnabled(workspace) ? 'cloud-sync' : 'cloud-rest';
  }
  return 'local';
}

/**
 * `{ workspace }` when this surface may act, or `{ error }` — evaluated BEFORE
 * anything is read, so a refusal has touched nothing.
 *
 * HYDRATION, honestly: `App.js` gates the persist effect on a React ref
 * (`snapshotHydratedRef`) that nothing outside the component can read. Under
 * the store-routed design that gap is survivable rather than dangerous — an
 * un-hydrated store means the effect early-returns, so the MCP write is LOST
 * when hydration overwrites it, not written over the user's data. It is still
 * a wrong answer to the agent, and it is on the hands-on checklist.
 */
export function currentWorkspaceOrRefuse() {
  const workspace = useWorkspaceStore.getState().currentWorkspace;
  if (!workspace) {
    return {
      error: mcpError(
        'unavailable',
        'No Apiboo workspace is open, so there is nothing to write to. Nothing was written. Ask the user to open a workspace.'
      ),
    };
  }
  const storage = storageKindOf(workspace);
  const authed = !!useAuthStore.getState().isAuthenticated;
  if (isGitWorkspace(workspace) || !usesLocalDataPath(workspace, authed)) {
    return {
      error: mcpError(
        'unavailable',
        `The open workspace stores its collections over ${storage}, which this server does not write to. Nothing was written. Ask the user to switch to a local workspace.`,
        { workspace: workspaceReport(workspace) }
      ),
    };
  }
  return { workspace };
}

/** The LIVE tree — the store, never disk. See the header. */
export function readCollections() {
  const raw = useCollectionsStore.getState().collections;
  return Array.isArray(raw) ? raw : [];
}

export function findCollection(collections, collectionId) {
  return collections.find((c) => String(c.id) === String(collectionId)) || null;
}

/**
 * A request by id, with the collection it lives in — ids are unique across the
 * workspace, and every read tool takes a `request_id` alone because that is
 * what `collections_list` hands back. Returns `{collection, request}` or
 * `null`.
 */
/** A folder by id, inside one collection. `null` when it is not there. */
export function findFolder(collection, folderId) {
  const wanted = String(folderId ?? '');
  if (!wanted) return null;
  return (
    (collection?.settings?.folders || []).find((f) => String(f.id) === wanted) || null
  );
}

export function findRequest(collections, requestId) {
  const wanted = String(requestId ?? '');
  if (!wanted) return null;
  for (const collection of collections) {
    const request = (collection.requests || []).find((r) => String(r.id) === wanted);
    if (request) return { collection, request };
  }
  return null;
}

/** UTF-8 byte length of a record's persisted `data`, for the per-op cap pre-check. */
export function recordBytes(record) {
  try {
    return new TextEncoder().encode(JSON.stringify(record ?? null)).length;
  } catch {
    // A record that cannot be serialised cannot be persisted either; reporting
    // it as over the cap refuses the write, which is the safe direction.
    return OUTBOX_ITEM_MAX_BYTES + 1;
  }
}

/**
 * THE write. `change(collections, workspace)` returns
 * `{ next, result }` — or `{ error }` to refuse — where `next` is the WHOLE
 * collections array and `result` is what the tool answers with.
 *
 * `next` must be DERIVED from the array handed in by a single-record change.
 * Rebuilding it from anything else is the failure mode of this whole surface:
 * a collection missing from `next` is tombstoned on the user's every device by
 * the persist that follows, and there is no undo. `assertNoCollectionLost`
 * below is the guard, and `mcpCollectionWrites.test.js`'s J-W1 is the test.
 */
export function applyCollectionsChange(change) {
  return serialize(async () => {
    const gate = currentWorkspaceOrRefuse();
    if (gate.error) return gate.error;
    const { workspace } = gate;

    // Plans v2: the other own local workspaces' counts, read BEFORE the tree
    // (the read awaits disk; nothing may sit between `before` and the write).
    // null = the local caps do not apply (paid plan, team or cloud workspace).
    const ownLocal = isLocalWorkspace(workspace, !!useAuthStore.getState().isAuthenticated) && !isTeamWorkspace(workspace);
    const others = ownLocal && (isCapped('collections') || isCapped('requests')) ? await otherLocalApiCounts() : null;

    const before = readCollections();
    let outcome;
    try {
      outcome = await change(before, workspace);
    } catch (err) {
      if (process.env.NODE_ENV !== 'test') console.warn('[mcp] collections change failed:', err);
      return mcpError('write_failed', 'The change could not be applied. Nothing was written.');
    }
    if (!outcome || outcome.error) return outcome?.error || mcpError('write_failed', 'The change could not be applied. Nothing was written.');

    const { next, result, sizedRecords, allowRemovals, tabPatches } = outcome;
    const lost = assertNoCollectionLost(before, next, allowRemovals);
    if (lost) {
      // Not an assertion for a developer — a refusal for the agent. Reaching
      // here means a change function tried to hand back an array that would
      // have deleted something, and the only safe answer is to write nothing.
      if (process.env.NODE_ENV !== 'test') console.warn('[mcp] refused a collections write that would drop:', lost);
      return mcpError(
        'write_failed',
        'That change would have removed collections it was not asked to remove, so nothing was written.'
      );
    }

    // Plans v2: an own cloud workspace is read-only without cloud_sync (the
    // server refuses every write there, 402 read_only); only a delete, which
    // the server still takes, goes through.
    const readOnly = readOnlyWorkspaceRefusal(workspace);
    if (readOnly && !allowRemovals) return mcpError('unavailable', `${readOnly} Nothing was written.`);

    // Plans v2: whatever the change adds (a create, an import, a duplicate)
    // counts against the plan's local caps, across every own LOCAL workspace
    // (the others through the UI's counter, plans/otherLocalApiCounts). A team
    // workspace's items are the team's and do not count.
    const had = countApiItems(before);
    const has = countApiItems(next);
    if (others) {
      for (const kind of ['collections', 'requests'].filter((k) => has[k] > had[k])) {
        const capped = capRefusal(kind, had[kind] + others[kind], has[kind] - had[kind]);
        if (capped) return mcpError('plan_limit', capped, { requiredPlan: 'basic' });
      }
    }

    // The per-op cap pre-check, on the records this change actually created. Rust
    // will not refuse; it will silently drop the sync op (see the constant).
    for (const record of sizedRecords || []) {
      const bytes = recordBytes(record);
      if (bytes > OUTBOX_ITEM_MAX_BYTES) {
        return mcpError(
          'record_too_large',
          "That record is too large to sync to the user's other devices, so NOTHING was written. Retrying will not help — send less content.",
          { bytes, cap: OUTBOX_ITEM_MAX_BYTES }
        );
      }
    }

    setCollections(next);

    // Read the store back. Cheap, and it turns "the setter silently did
    // nothing" — which would otherwise be reported to the agent as a landed
    // write — into a visible failure.
    const applied = readCollections();
    if (applied !== next) {
      return mcpError(
        'write_failed',
        'The change did not reach the workspace, so nothing was written.'
      );
    }

    // **PLAN §F2.** The store now agrees; the OPEN EDITOR does not, and a tab
    // holds a full denormalized copy of the request that its next Ctrl+S
    // writes back whole. Patching it here rather than in each handler is
    // deliberate: this is the one write path, so a handler cannot forget it,
    // and the patch happens after the store write so the two can never
    // disagree about what landed. See `mcp/requestTabSync.js`.
    //
    // Best-effort in exactly the sense `uiRefresh`'s reveal is: a throwing
    // store listener must not turn a landed write into `write_failed`.
    let tabsPatched = 0;
    for (const patch of tabPatches || []) {
      try {
        if (patchOpenRequestTab(patch.requestId, patch.patch)) tabsPatched += 1;
      } catch (err) {
        if (process.env.NODE_ENV !== 'test') console.warn('[mcp] tab patch failed:', err);
      }
    }

    return {
      ...result,
      workspace: workspaceReport(workspace),
      // Reported rather than inferred, and ONLY for a change that could have
      // touched an editor: an agent that changed a request the user has open
      // should be able to say so, and a `0` here is the honest answer to "did
      // the editor see this" rather than silence. A create has no open tab by
      // construction, so its result shape is unchanged from tranche 1.
      ...(tabPatches ? { openEditorsUpdated: tabsPatched } : {}),
    };
  });
}

/**
 * Every collection id present before must still be present after, and no
 * collection may lose requests it was not the subject of. Returns a
 * description of what would be lost, or `null`.
 *
 * This exists because the blast radius of a bug on this surface is the whole
 * `collections` family: the persist that follows hands the array to
 * `workspace_db_save_snapshot`, which tombstones every row absent from it,
 * locally and through the outbox to every device.
 */
export function assertNoCollectionLost(before, after, allowRemovals) {
  if (!Array.isArray(after)) return 'the change did not return an array';
  // **Tranche 5.** A delete legitimately removes rows, so it DECLARES exactly
  // which ids it intends to remove and this guard exempts those and only
  // those. That keeps the guard load-bearing on the deletes rather than
  // switching it off for them: a cascade that took one node more than it told
  // the user about is still refused, and so is a delete whose plan and whose
  // array disagree. An UPDATE or a CREATE declares nothing and may remove
  // nothing at all.
  const allowedCollections = new Set((allowRemovals?.collections || []).map(String));
  const allowedRequests = new Set((allowRemovals?.requests || []).map(String));

  const afterIds = new Set(after.map((c) => String(c.id)));
  const missing = before
    .map((c) => String(c.id))
    .filter((id) => !afterIds.has(id) && !allowedCollections.has(id));
  if (missing.length) return `collections ${missing.join(', ')}`;

  const afterById = new Map(after.map((c) => [String(c.id), c]));
  for (const col of before) {
    const now = afterById.get(String(col.id));
    // A collection this change was allowed to delete has no "after" to compare
    // against; its requests went with it and were declared with it.
    if (!now) continue;
    const had = new Set((col.requests || []).map((r) => String(r.id)));
    const has = new Set((now.requests || []).map((r) => String(r.id)));
    const gone = [...had].filter((id) => !has.has(id) && !allowedRequests.has(id));
    if (gone.length) return `requests ${gone.join(', ')} in collection ${col.id}`;
  }
  return null;
}

/**
 * `expected_rev` discipline, in one place so every tool that needs it phrases
 * the refusal identically.
 *
 * Returns an error object, or `null` when the caller may proceed. See
 * `mcp/collectionRev.js` for what a rev on THIS surface is and — more
 * importantly — what it is not.
 */
export function requireRev(expected, actual, what) {
  if (expected == null || expected === '') {
    return mcpError(
      'expected_rev_required',
      `Changing ${what} requires expected_rev — read it first and pass back the rev you read. Nothing was written.`
    );
  }
  if (typeof expected !== 'string' || !REV_RE.test(expected)) {
    return mcpError('invalid_argument', '`expected_rev` must be the 16-character rev you read.', {
      expectedRev: String(expected),
    });
  }
  if (expected !== actual) {
    return mcpError(
      'conflict',
      `That ${what} changed since you read it, so nothing was written. Read it again and retry with the new rev.`,
      { rev: actual }
    );
  }
  return null;
}
